var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import classnames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { tokens } from '@servicetitan/tokens/core';
import { Overlay } from '../../internal/Overlay';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { Headline } from '../Headline';
import { Button } from '../Button';
export const ModalContent = ({ children, className, scrollable = true, id, }) => {
    const ModalContentClassNames = classnames('Modal__content', className, {
        'Modal__content--scrollable': scrollable,
    });
    return (React.createElement("div", { id: id, className: ModalContentClassNames }, children));
};
ModalContent.displayName = 'ModalContent';
export const ModalFooter = (_a) => {
    var { align = 'right', children, className } = _a, props = __rest(_a, ["align", "children", "className"]);
    const ModalFooterClassNames = classnames('Modal__footer', className, {
        'Modal__footer--left': align === 'left',
        'Modal__footer--center': align === 'center',
        'Modal__footer--right': align === 'right',
        'Modal__footer--space-between': align === 'space-between',
    });
    return (React.createElement("div", Object.assign({ className: ModalFooterClassNames }, props), children));
};
ModalFooter.displayName = 'ModalFooter';
export const ModalHeader = (_a) => {
    var { className, title, onClose, id } = _a, props = __rest(_a, ["className", "title", "onClose", "id"]);
    if (!onClose && title == null) {
        return null;
    }
    const ModalHeaderClassNames = classnames('Modal__header', className);
    return (React.createElement("div", Object.assign({ className: ModalHeaderClassNames }, props),
        React.createElement(Headline, { className: "Modal__header-title", id: id }, title),
        onClose && (React.createElement(Button, { "aria-label": "close modal", fill: "subtle", className: "Modal__header-close", iconName: "clear", size: "xsmall", onClick: onClose }))));
};
ModalHeader.displayName = 'ModalHeader';
/**
 * Modal Sizes
 */
export var ModalSizes;
(function (ModalSizes) {
    ModalSizes["XS"] = "XS";
    ModalSizes["S"] = "S";
    ModalSizes["M"] = "M";
    ModalSizes["L"] = "L";
})(ModalSizes || (ModalSizes = {}));
function sizeClass(size) {
    return `Modal--${size}`;
}
export const ModalWindow = (_a) => {
    var { animate = true, children, className, focusTrapOptions, onClose, open, portal = true, size = ModalSizes.M } = _a, props = __rest(_a, ["animate", "children", "className", "focusTrapOptions", "onClose", "open", "portal", "size"]);
    const ModalWindowClassNames = classnames('Modal', sizeClass(size), className);
    // Focus trapping
    const containerRef = useFocusTrap(Object.assign({ autoFocusContainer: true, disabled: !open }, focusTrapOptions));
    const duration = parseInt(tokens.durationStandard, 10) / 1000; // '200ms' -> 0.2
    const framerAnimation = {
        initial: { opacity: 0, y: 50 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 50 },
        transition: { type: 'easeInOut', duration },
    };
    const Animate = animate ? AnimatePresence : React.Fragment;
    const ModalEl = animate ? motion.div : 'div';
    const ModalProps = Object.assign(Object.assign(Object.assign({ className: ModalWindowClassNames }, (animate && framerAnimation)), props), { ref: containerRef });
    return (React.createElement(Overlay, { animate: animate, backdrop: true, onClose: onClose, open: open, portal: portal },
        React.createElement(Animate, null, open && React.createElement(ModalEl, Object.assign({}, ModalProps), children))));
};
//# sourceMappingURL=components.js.map